import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  HStack,
  Icon,
  Input,
} from '@eluve/components';

const REMEMBER_EMAIL_KEY = 'remember_email';

const emailFormSchema = z.object({
  email: z.string().email(),
});

export type EmailFormData = z.infer<typeof emailFormSchema>;

export interface EmailEntryFormProps {
  onSubmit: (data: EmailFormData) => Promise<void>;
  isLoading?: boolean;
  defaultEmail?: string;
}

export const EmailEntryForm: React.FC<EmailEntryFormProps> = ({
  onSubmit,
  isLoading = false,
  defaultEmail = '',
}) => {
  const [rememberEmailChecked, setRememberEmailChecked] = useState(false);

  const form = useForm<EmailFormData>({
    resolver: zodResolver(emailFormSchema),
    defaultValues: { email: defaultEmail },
  });

  const email = form.watch('email');

  useEffect(() => {
    if (!defaultEmail) {
      try {
        const stored = localStorage.getItem(REMEMBER_EMAIL_KEY);
        if (stored) {
          form.setValue('email', stored, { shouldValidate: true });
          setRememberEmailChecked(true);
        }
      } catch {
        // ignore storage errors
      }
    }
  }, [form, defaultEmail]);

  const handleSubmit = async (data: EmailFormData) => {
    try {
      // Handle remember me functionality
      if (rememberEmailChecked) {
        // Remember Me is not storing PII that is unexpected
        // no-dd-sa:typescript-browser-security/local-storage-sensitive-data
        localStorage.setItem(REMEMBER_EMAIL_KEY, data.email);
      } else {
        localStorage.removeItem(REMEMBER_EMAIL_KEY);
      }
    } catch {
      // ignore storage errors
    }

    await onSubmit(data);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && email) {
      form.handleSubmit(handleSubmit)();
    }
  };

  return (
    <Form {...form}>
      <form
        className="w-full space-y-4"
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs text-text-tertiary">
                Email
              </FormLabel>
              <FormControl>
                <div className="relative mt-0">
                  <div className="pointer-events-none absolute left-2 top-1/2 mr-0.5 -translate-y-1/2 rounded bg-bg-gray-subtle p-1.5">
                    <Icon name="Mail" className="size-4 text-text-tertiary" />
                  </div>
                  <Input
                    placeholder="you@company.com"
                    {...field}
                    onKeyDown={handleKeyDown}
                    className="pl-11"
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? 'Validating Email ...' : 'Continue'}
        </Button>
        <HStack align="center" className="pt-1">
          <input
            id="remember-email"
            type="checkbox"
            className="size-4 rounded border-border-primary text-text-tertiary focus:ring-0"
            checked={rememberEmailChecked}
            onChange={(e) => setRememberEmailChecked(e.target.checked)}
          />
          <label
            htmlFor="remember-email"
            className="cursor-pointer select-none text-sm text-text-tertiary"
          >
            Remember my email
          </label>
        </HStack>
      </form>
    </Form>
  );
};
